import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, CreditCard, Calendar, ShieldCheck, Trash2, Crown, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { StatCard, fmtMoney } from "@/components/admin/AdminShared";
import { AdminUsersTab } from "@/components/admin/AdminUsersTab";
import { AdminPaymentsTab } from "@/components/admin/AdminPaymentsTab";
import { AdminMessagesTab, MessageDialog } from "@/components/admin/AdminMessagesTab";

export default function AdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [tab, setTab] = useState("users");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [openMessage, setOpenMessage] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, u, p, m] = await Promise.all([
        api.get("/admin/stats"),
        api.get(`/admin/users?q=${encodeURIComponent(q)}`),
        api.get("/admin/payments"),
        api.get("/admin/messages"),
      ]);
      setStats(s.data);
      setUsers(u.data.users || []);
      setPayments(p.data.payments || []);
      setMessages(m.data.messages || []);
      setUnreadCount(m.data.unread_count || 0);
    } catch (err) {
      toast.error("Impossible de charger les données admin");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_super_admin) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const onSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users?q=${encodeURIComponent(q)}`);
      setUsers(data.users || []);
    } catch { toast.error("Recherche échouée"); }
    finally { setLoading(false); }
  };

  const toggleDisable = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}`, { disabled: !u.disabled });
      toast.success(u.disabled ? "Compte réactivé" : "Compte désactivé");
      loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Échec"); }
  };

  const upgradePlan = async (u, newPlan) => {
    try {
      const body = { plan: newPlan };
      if (newPlan === "business") {
        const d = new Date();
        d.setDate(d.getDate() + 30);
        body.plan_expires_at = d.toISOString();
      }
      await api.put(`/admin/users/${u.id}`, body);
      toast.success(`Plan mis à jour → ${newPlan}`);
      loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Échec"); }
  };

  const deleteUser = async (u) => {
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Compte ${u.email} supprimé`);
      setConfirmDelete(null);
      loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Échec"); }
  };

  const toggleMessageRead = async (msg, read) => {
    try {
      await api.put(`/admin/messages/${msg.id}`, { read });
      setMessages((arr) => arr.map((m) => (m.id === msg.id ? { ...m, read } : m)));
      setUnreadCount((c) => Math.max(0, c + (read ? -1 : 1)));
      if (openMessage?.id === msg.id) setOpenMessage((m) => ({ ...m, read }));
    } catch (err) { toast.error("Échec"); }
  };

  const deleteMessage = async (msg) => {
    try {
      await api.delete(`/admin/messages/${msg.id}`);
      setMessages((arr) => arr.filter((m) => m.id !== msg.id));
      if (!msg.read) setUnreadCount((c) => Math.max(0, c - 1));
      setOpenMessage(null);
      toast.success("Message supprimé");
    } catch (err) { toast.error("Échec"); }
  };

  const openAndMarkRead = (msg) => {
    setOpenMessage(msg);
    if (!msg.read) toggleMessageRead(msg, true);
  };

  if (!user) return null;
  if (!user.is_super_admin) return <Navigate to="/app/dashboard" replace />;

  const tabs = [
    { id: "users", label: `Utilisateurs (${users.length})` },
    { id: "payments", label: `Paiements (${payments.length})` },
    {
      id: "messages",
      label: (
        <span className="inline-flex items-center gap-2">
          Messages ({messages.length})
          {unreadCount > 0 && (
            <span
              className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-terracotta text-white text-[10px] font-bold"
              data-testid="admin-messages-unread-badge"
            >
              {unreadCount}
            </span>
          )}
        </span>
      ),
    },
  ];

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="admin-page">
      <div className="mb-10 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-2 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" /> Administration
          </div>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl">Tableau de bord admin</h1>
          <p className="text-af-secondary mt-1">Gérez les utilisateurs, les abonnements et les paiements AfriMeet.</p>
        </div>
        <Button variant="outline" onClick={loadAll} className="border-af rounded-md" data-testid="admin-refresh-btn">
          <RefreshCw className={"h-4 w-4 mr-2 " + (loading ? "animate-spin" : "")} /> Actualiser
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
          <StatCard icon={Users} label="Utilisateurs" value={stats.users_total} />
          <StatCard icon={Crown} label="Plan Business" value={stats.users_business} accent />
          <StatCard icon={Calendar} label="Réunions créées" value={stats.meetings_total} />
          <StatCard icon={CreditCard} label="Revenu total" value={fmtMoney(stats.revenue_total_xof)} accent />
        </div>
      )}

      <div className="flex gap-2 mb-6 border-b border-af">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            onClick={() => setTab(tb.id)}
            data-testid={`admin-tab-${tb.id}`}
            className={
              "px-4 py-2.5 -mb-px font-semibold text-sm border-b-2 transition-colors " +
              (tab === tb.id
                ? "border-terracotta text-terracotta"
                : "border-transparent text-af-secondary hover:text-af")
            }
          >
            {tb.label}
          </button>
        ))}
      </div>

      {tab === "users" && (
        <AdminUsersTab
          users={users}
          q={q}
          setQ={setQ}
          onSearch={onSearch}
          onToggleDisable={toggleDisable}
          onUpgradePlan={upgradePlan}
          onConfirmDelete={setConfirmDelete}
        />
      )}
      {tab === "payments" && <AdminPaymentsTab payments={payments} />}
      {tab === "messages" && <AdminMessagesTab messages={messages} onOpenMessage={openAndMarkRead} />}

      {openMessage && (
        <MessageDialog
          message={openMessage}
          onClose={() => setOpenMessage(null)}
          onToggleRead={toggleMessageRead}
          onDelete={deleteMessage}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <Card className="bg-af-card border-af rounded-md p-7 max-w-md w-full" data-testid="confirm-delete-dialog">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="h-6 w-6 text-[#9F1239] shrink-0" />
              <div>
                <h3 className="font-display font-bold text-lg mb-1">Supprimer le compte ?</h3>
                <p className="text-sm text-af-secondary">
                  Cette action supprime définitivement <strong>{confirmDelete.email}</strong>, ses réunions et son espace de travail. Cela ne peut pas être annulé.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={() => setConfirmDelete(null)}>Annuler</Button>
              <Button onClick={() => deleteUser(confirmDelete)} data-testid="confirm-delete-btn"
                className="bg-[#9F1239] hover:bg-[#7F1D2E] text-white rounded-md">
                <Trash2 className="h-4 w-4 mr-2" /> Supprimer
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
